// shift-swap-request.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  FormsModule,
} from '@angular/forms';
import {
  Firestore,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  orderBy,
  doc,
  updateDoc,
  collectionData,
} from '@angular/fire/firestore';
import { AuthService } from 'src/app/auth.service';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FirebaseService } from 'src/app/services/firebase.service';

interface PublishedSchedule {
  id: string;
  weekStart: string;
  weekEnd: string;
  weekRange: string;
  publishedAt: any;
  result: any[];
  dates: { [key: string]: string };
}

interface ShiftSelection {
  scheduleId: string;
  scheduleName: string;
  date: string;
  dayKey: string;
  shift: string;
  employeeName: string;
}

interface ShiftSwapRequest {
  id?: string;
  orgId: string;
  requestedBy: string;
  requestedByEmail?: string;

  shift1: {
    scheduleId: string;
    scheduleName: string;
    date: string;
    dayKey: string;
    originalShift: string;
    employeeName: string;
  };

  shift2: {
    scheduleId: string;
    scheduleName: string;
    date: string;
    dayKey: string;
    originalShift: string;
    employeeName: string;
  };

  status: 'pending_employee' | 'pending_manager' | 'approved' | 'declined';
  employeeApproved: boolean;
  employeeApprovedAt?: any;
  managerApproved: boolean;
  managerApprovedBy?: string;
  managerApprovedAt?: any;
  declinedBy?: string;
  declinedReason?: string;
  createdAt: any;
  notes?: string;
}

@Component({
  selector: 'app-shift-swap-request',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule],
  templateUrl: './shift-swap-request.component.html',
  styleUrls: ['./shift-swap-request.component.css'],
})
export class ShiftSwapRequestComponent implements OnInit {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();
  private readonly firebaseService = inject(FirebaseService); // Add this line

  // View Mode: 'create' | 'my-requests' | 'pending-approval'
  viewMode: 'create' | 'my-requests' | 'pending-approval' = 'create';

  // Data
  publishedSchedules: PublishedSchedule[] = [];
  mySwapRequests: ShiftSwapRequest[] = [];
  pendingApprovals: ShiftSwapRequest[] = []; // Requests where current user needs to approve
  managerPendingApprovals: ShiftSwapRequest[] = []; // For managers
  selectedDayFilter: string = 'all'; // For filtering by day
  selectedEmployeeFilter: string = 'all'; // For filtering by employee
  availableEmployees: string[] = []; // List of employees for filter

  currentStep = 1; // 1: select your shift, 2: select desired shift, 3: review & submit

  // Selections
  myShift: ShiftSelection | null = null;
  desiredShift: ShiftSelection | null = null;

  // State
  isLoading = false;
  isSubmitting = false;
  employeeName = '';
  employeeEmail = '';
  orgId = '';
  userRole = '';

  // Form
  swapRequestForm = this.fb.nonNullable.group({
    notes: ['', Validators.maxLength(500)],
  });

  declineForm = this.fb.nonNullable.group({
    reason: ['', Validators.required],
  });

  selectedRequestForAction: ShiftSwapRequest | null = null;

  // Toast
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  readonly dayKeys = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  readonly dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  async ngOnInit() {
    const user = this.auth.getUser();
    this.employeeName = this.auth.getUserName();
    this.employeeEmail = user?.email || '';
    this.orgId = this.auth.getUsersOrgId();
    this.userRole = user?.role || 'employee';

    // console.log('👤 Current user name:', this.employeeName);

    if (!this.employeeName || !this.orgId) {
      this.showToast('Please log in to request shift swaps', 'error');
      setTimeout(() => this.router.navigate(['/login']), 2000);
      return;
    }

    await this.loadPublishedSchedules();
    await this.loadMySwapRequests();
    await this.loadPendingApprovals();

    if (this.isManager) {
      await this.loadManagerPendingApprovals();
    }
  }

  get isManager(): boolean {
    return this.userRole === 'manager' || this.userRole === 'admin';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadPublishedSchedules() {
    this.isLoading = true;
    try {
      const schedulesRef = collection(this.firestore, 'schedules');
      const q = query(
        schedulesRef,
        where('orgId', '==', this.orgId),
        where('status', '==', 'published'),
        orderBy('weekStart', 'desc')
      );

      const snapshot = await getDocs(q);
      this.publishedSchedules = snapshot.docs.map((doc) => ({
        id: doc.id,
        weekStart: doc.data()['weekStart'],
        weekEnd: doc.data()['weekEnd'],
        weekRange: doc.data()['weekRange'],
        publishedAt: doc.data()['publishedAt'],
        result: doc.data()['result'] || [],
        dates: doc.data()['dates'] || {},
      }));
    } catch (error) {
      console.error('Error loading schedules:', error);
      this.showToast('Failed to load schedules', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  private async loadMySwapRequests() {
    try {
      const swapRef = collection(this.firestore, 'shiftSwapRequests');

      const q = query(
        swapRef,
        where('orgId', '==', this.orgId),
        where('requestedBy', '==', this.employeeName)
      );

      collectionData(q, { idField: 'id' })
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          // ✅ Sort in memory
          this.mySwapRequests = (data as ShiftSwapRequest[]).sort((a, b) => {
            const aTime = a.createdAt?.toMillis() || 0;
            const bTime = b.createdAt?.toMillis() || 0;
            return bTime - aTime;
          });
        });
    } catch (error) {
      console.error('Error loading my swap requests:', error);
    }
  }

  private async loadPendingApprovals() {
    try {
      const swapRef = collection(this.firestore, 'shiftSwapRequests');

      const q = query(
        swapRef,
        where('orgId', '==', this.orgId),
        where('shift2.employeeName', '==', this.employeeName),
        where('status', '==', 'pending_employee')
        // orderBy removed - will sort in memory instead
      );

      collectionData(q, { idField: 'id' })
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          // ✅ Sort in memory after receiving data
          this.pendingApprovals = (data as ShiftSwapRequest[]).sort((a, b) => {
            const aTime = a.createdAt?.toMillis() || 0;
            const bTime = b.createdAt?.toMillis() || 0;
            return bTime - aTime; // newest first
          });
        });
    } catch (error) {
      console.error('Error loading pending approvals:', error);
    }
  }

  private async loadManagerPendingApprovals() {
    try {
      const swapRef = collection(this.firestore, 'shiftSwapRequests');

      const q = query(
        swapRef,
        where('orgId', '==', this.orgId),
        where('status', '==', 'pending_manager')
        // orderBy removed - will sort in memory instead
      );

      collectionData(q, { idField: 'id' })
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          // ✅ Sort in memory after receiving data
          this.managerPendingApprovals = (data as ShiftSwapRequest[]).sort(
            (a, b) => {
              const aTime = a.createdAt?.toMillis() || 0;
              const bTime = b.createdAt?.toMillis() || 0;
              return bTime - aTime; // newest first
            }
          );
        });
    } catch (error) {
      console.error('Error loading manager pending approvals:', error);
    }
  }

  switchView(mode: 'create' | 'my-requests' | 'pending-approval') {
    this.viewMode = mode;
    this.resetSelection();
  }

  // Get employee's shifts from a specific schedule
  getMyShiftsFromSchedule(schedule: PublishedSchedule): any[] {
    const employee = schedule.result.find(
      (e: any) =>
        e.name?.trim().toLowerCase() === this.employeeName.trim().toLowerCase()
    );

    if (!employee) return [];

    // ✅ Set today's date at midnight for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Use numbered indices (0-6) instead of day keys for dates
    const datesArray = [
      schedule.dates['0'], // Sunday
      schedule.dates['1'], // Monday
      schedule.dates['2'], // Tuesday
      schedule.dates['3'], // Wednesday
      schedule.dates['4'], // Thursday
      schedule.dates['5'], // Friday
      schedule.dates['6'], // Saturday
    ];

    return this.dayKeys
      .map((dayKey, index) => {
        const shiftDate = datesArray[index];
        const shiftDateObj = shiftDate
          ? new Date(shiftDate + 'T00:00:00')
          : null;

        const shift = employee[dayKey] || 'Off';
        const isOff = !employee[dayKey] || 
                      employee[dayKey].toLowerCase() === 'off' || 
                      employee[dayKey].toLowerCase() === 'requested off';

        return {
          dayKey,
          dayName: this.dayNames[index],
          shift: shift,
          date: shiftDate || '',
          dateObj: shiftDateObj,
          isOff: isOff,
          isPast: shiftDateObj ? shiftDateObj < today : false,
        };
      })
      .filter((day) => !day.isPast); // ✅ Only filter out past shifts, keep off days
  }

  getAllShiftsFromSchedule(schedule: PublishedSchedule): any[] {
    const shifts: any[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // ✅ FIX: Use numbered indices for dates
    const datesArray = [
      schedule.dates['0'], // Sunday
      schedule.dates['1'], // Monday
      schedule.dates['2'], // Tuesday
      schedule.dates['3'], // Wednesday
      schedule.dates['4'], // Thursday
      schedule.dates['5'], // Friday
      schedule.dates['6'], // Saturday
    ];

    schedule.result.forEach((employee: any) => {
      if (
        employee.name?.trim().toLowerCase() ===
        this.employeeName.trim().toLowerCase()
      ) {
        return; // Skip current user
      }

      this.dayKeys.forEach((dayKey, index) => {
        const shift = employee[dayKey];
        const shiftDate = datesArray[index]; // Use numbered index

        // ✅ NEW: Include ALL shifts, including "off" days
        // Only exclude if there's NO shift data at all
        if (shift && shiftDate) {
          const shiftDateObj = new Date(shiftDate + 'T00:00:00');

          // Only include shifts that are today or in the future
          if (shiftDateObj >= today) {
            // ✅ Determine if it's a day off
            const isOff = 
              shift.toLowerCase() === 'off' || 
              shift.toLowerCase() === 'requested off';

            shifts.push({
              employeeName: employee.name,
              dayKey,
              dayName: this.dayNames[index],
              shift,
              date: shiftDate,
              dateObj: shiftDateObj,
              isOff: isOff, // ✅ Add flag to identify off days
            });
          }
        }
      });
    });

    return shifts.sort((a, b) => a.dateObj.getTime() - b.dateObj.getTime()); // Sort by date
  }

  // Update selectMyShift to call updateAvailableEmployees
  selectMyShift(
    schedule: PublishedSchedule,
    dayKey: string,
    shift: string,
    date: string
  ) {
    this.myShift = {
      scheduleId: schedule.id,
      scheduleName: this.formatWeekLabel(schedule),
      date,
      dayKey,
      shift,
      employeeName: this.employeeName,
    };
    this.currentStep = 2;
    this.updateAvailableEmployees(); // Populate filter options
    this.selectedDayFilter = 'all'; // Reset filters
    this.selectedEmployeeFilter = 'all';
  }

  selectDesiredShift(
    schedule: PublishedSchedule,
    dayKey: string,
    shift: string,
    date: string,
    employeeName: string
  ) {
    this.desiredShift = {
      scheduleId: schedule.id,
      scheduleName: this.formatWeekLabel(schedule),
      date,
      dayKey,
      shift,
      employeeName,
    };
    this.currentStep = 3;
  }

  async submitSwapRequest() {
    if (!this.myShift || !this.desiredShift) return;

    this.isSubmitting = true;
    try {
      const swapRequest = {
        orgId: this.orgId,
        requestedBy: this.employeeName,
        requestedByEmail: this.employeeEmail,

        shift1: {
          scheduleId: this.myShift.scheduleId,
          scheduleName: this.myShift.scheduleName,
          date: this.myShift.date,
          dayKey: this.myShift.dayKey,
          originalShift: this.myShift.shift,
          employeeName: this.myShift.employeeName,
        },

        shift2: {
          scheduleId: this.desiredShift.scheduleId,
          scheduleName: this.desiredShift.scheduleName,
          date: this.desiredShift.date,
          dayKey: this.desiredShift.dayKey,
          originalShift: this.desiredShift.shift,
          employeeName: this.desiredShift.employeeName,
        },

        status: 'pending_employee',
        employeeApproved: false,
        managerApproved: false,
        createdAt: serverTimestamp(),
        notes: this.swapRequestForm.value.notes || '',
      };

      await addDoc(
        collection(this.firestore, 'shiftSwapRequests'),
        swapRequest
      );

      this.showToast(
        'Shift swap request submitted! Waiting for employee approval.',
        'success'
      );
      this.resetSelection();
      this.viewMode = 'my-requests';
    } catch (error) {
      console.error('Error submitting swap request:', error);
      this.showToast('Failed to submit request', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  // Employee approves the swap request
  async approveAsEmployee(request: ShiftSwapRequest) {
    if (!request.id) return;

    try {
      const requestRef = doc(this.firestore, 'shiftSwapRequests', request.id);
      await updateDoc(requestRef, {
        employeeApproved: true,
        employeeApprovedAt: serverTimestamp(),
        status: 'pending_manager',
      });

      this.showToast('Swap approved! Now pending manager approval.', 'success');
    } catch (error) {
      console.error('Error approving swap:', error);
      this.showToast('Failed to approve swap', 'error');
    }
  }

  // Manager approves the swap request
  async approveAsManager(request: ShiftSwapRequest) {
    if (!request.id) return;

    try {
      // 1️⃣ Update the swap request status
      const requestRef = doc(this.firestore, 'shiftSwapRequests', request.id);
      await updateDoc(requestRef, {
        managerApproved: true,
        managerApprovedBy: this.employeeName,
        managerApprovedAt: serverTimestamp(),
        status: 'approved',
      });

      // 2️⃣ Update the published schedule with the swapped shifts
      try {
        // Import your FirebaseService at the top of the file
        await this.firebaseService.swapShiftsInPublishedSchedule(
          request.shift1.scheduleId, // The schedule ID
          request.shift1.employeeName, // Employee 1's name
          request.shift2.employeeName, // Employee 2's name
          request.shift1.dayKey // The day to swap (both should be same day in most cases)
        );

        // If the shifts are on different days, swap both
        if (request.shift1.dayKey !== request.shift2.dayKey) {
          // Swap employee 1's shift on day 1 with employee 2's shift on day 2
          await this.firebaseService.swapShiftsInPublishedSchedule(
            request.shift2.scheduleId,
            request.shift1.employeeName,
            request.shift2.employeeName,
            request.shift2.dayKey
          );
        }

        this.showToast('Swap approved and schedule updated!', 'success');
      } catch (scheduleError) {
        console.error('Error updating schedule:', scheduleError);
        this.showToast('Swap approved but schedule update failed', 'error');
      }
    } catch (error) {
      console.error('Error approving swap:', error);
      this.showToast('Failed to approve swap', 'error');
    }
  }

  // Decline swap (can be done by employee or manager)
  openDeclineModal(request: ShiftSwapRequest) {
    this.selectedRequestForAction = request;
    this.declineForm.reset();
  }

  async declineSwapRequest() {
    if (!this.selectedRequestForAction?.id || this.declineForm.invalid) return;

    try {
      const requestRef = doc(
        this.firestore,
        'shiftSwapRequests',
        this.selectedRequestForAction.id
      );
      await updateDoc(requestRef, {
        status: 'declined',
        declinedBy: this.employeeName,
        declinedReason: this.declineForm.value.reason,
      });

      this.showToast('Swap request declined', 'success');
      this.selectedRequestForAction = null;
    } catch (error) {
      console.error('Error declining swap:', error);
      this.showToast('Failed to decline swap', 'error');
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'pending_employee':
        return 'bg-warning text-dark';
      case 'pending_manager':
        return 'bg-info';
      case 'approved':
        return 'bg-success';
      case 'declined':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'pending_employee':
        return 'Pending Employee Approval';
      case 'pending_manager':
        return 'Pending Manager Approval';
      case 'approved':
        return 'Approved';
      case 'declined':
        return 'Declined';
      default:
        return status;
    }
  }

  resetSelection() {
    this.myShift = null;
    this.desiredShift = null;
    this.currentStep = 1;
    this.swapRequestForm.reset();
  }

  goBack() {
    if (this.currentStep > 1) {
      this.currentStep--;
      if (this.currentStep === 1) {
        this.desiredShift = null;
      }
    } else {
      this.router.navigate(['/public-schedule']);
    }
  }

  formatWeekLabel(schedule: PublishedSchedule): string {
    const start = new Date(schedule.weekStart + 'T00:00:00').toLocaleDateString(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
      }
    );
    const end = new Date(schedule.weekEnd + 'T00:00:00').toLocaleDateString(
      'en-US',
      {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }
    );
    return `${start} - ${end}`;
  }

  showToast(message: string, type: 'success' | 'error') {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    setTimeout(() => (this.toastVisible = false), 4000);
  }

  // Add method to get all available shifts across all schedules
  getAllAvailableShifts(): any[] {
    const allShifts: any[] = [];

    this.publishedSchedules.forEach((schedule) => {
      const scheduleShifts = this.getAllShiftsFromSchedule(schedule).map(
        (shift) => ({
          ...shift,
          scheduleId: schedule.id,
          scheduleName: this.formatWeekLabel(schedule),
        })
      );
      allShifts.push(...scheduleShifts);
    });

    return allShifts;
  }

  // Add method to get filtered shifts
  getFilteredShifts(): any[] {
    let shifts = this.getAllAvailableShifts();

    // Filter by employee
    if (this.selectedEmployeeFilter !== 'all') {
      shifts = shifts.filter(
        (s) => s.employeeName === this.selectedEmployeeFilter
      );
    }

    // Filter by day
    if (this.selectedDayFilter !== 'all') {
      shifts = shifts.filter((s) => s.dayKey === this.selectedDayFilter);
    }

    return shifts;
  }

  // Add method to populate employee filter options
  updateAvailableEmployees(): void {
    const employeeSet = new Set<string>();
    this.getAllAvailableShifts().forEach((shift) => {
      employeeSet.add(shift.employeeName);
    });
    this.availableEmployees = Array.from(employeeSet).sort();
  }

  // Update selectDesiredShift to accept schedule parameter
  selectDesiredShiftFromFiltered(shift: any) {
    this.desiredShift = {
      scheduleId: shift.scheduleId,
      scheduleName: shift.scheduleName,
      date: shift.date,
      dayKey: shift.dayKey,
      shift: shift.shift,
      employeeName: shift.employeeName,
    };
    this.currentStep = 3;
  }

  // Add reset filters method
  resetFilters(): void {
    this.selectedDayFilter = 'all';
    this.selectedEmployeeFilter = 'all';
  }
}
